'use client';

import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';

/**
 * Blocks rendering until the MSW service worker is ready.
 *
 * Only rendered in development (see providers.tsx). The worker itself
 * is gated on NEXT_PUBLIC_ENABLE_MOCKS=true — when the flag is unset
 * startMockWorker() resolves immediately and children render without delay.
 */
export function MSWProvider({ children }: { children: React.ReactNode }) {
	const [ready, setReady] = useState(false);
	const [Picker, setPicker] = useState<ComponentType | null>(null);

	useEffect(() => {
		if (!ready) {
			startMockWorker()
				.then(async (started) => {
					if (!started) return;
					const mod = await import('src/mocks/quote-scenario-picker');
					setPicker(() => mod.default);
				})
				.catch((err) => {
					console.error('[MSW] Failed to start mock worker:', err);
				})
				.finally(() => setReady(true));
		}
	}, [ready]);

	if (!ready) return null;

	return (
		<>
			{children}
			{Picker ? <Picker /> : null}
		</>
	);
}

async function startMockWorker(): Promise<boolean> {
	if (
		process.env.NODE_ENV !== 'development' ||
		process.env.NEXT_PUBLIC_ENABLE_MOCKS !== 'true'
	) {
		return false;
	}

	const { worker } = await import('src/mocks/browser');
	await worker.start({ onUnhandledRequest: 'bypass' });
	return true;
}
